// ViewCustomSchool.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../config";
import { Helmet } from "react-helmet";

export default function ViewCustomSchool() {
  const navigate = useNavigate();

  // If user did not come from chart, clear chart filters
  if (!localStorage.getItem("viewFromChart")) {
    localStorage.removeItem("viewCustomFilters");
    // keep viewSchoolsFilters untouched
  }

  // PRIORITIZE chart filters (viewCustomFilters) over viewSchoolsFilters
  const savedRaw = localStorage.getItem("viewCustomFilters") || localStorage.getItem("viewSchoolsFilters") || "{}";
  const saved = savedRaw ? JSON.parse(savedRaw) : {};

  // remove one-time flag
  if (localStorage.getItem("viewFromChart")) {
    localStorage.removeItem("viewFromChart");
  }

  const getCurrentFY = () => {
    const today = new Date();
    const yr = today.getFullYear();
    const mo = today.getMonth() + 1;
    return mo < 4 ? `${yr - 1}-${yr}` : `${yr}-${yr + 1}`;
  };

  const currentFY = getCurrentFY();

  // Applied (initial) filters used to initialize UI and API calls
  const appliedDistrict = saved.district || "0";
  const appliedActivity = saved.activity || "0";
  const appliedFY = saved.fy || currentFY;
  const appliedStatus = saved.status || "submitted";

  // UI state
  const [selectedDistrict, setSelectedDistrict] = useState(String(appliedDistrict));
  const [selectedActivity, setSelectedActivity] = useState(String(appliedActivity));
  const [selectedFY, setSelectedFY] = useState(appliedFY);
  const [selectedStatus, setSelectedStatus] = useState(appliedStatus);

  const [districts, setDistricts] = useState([]);
  const [activities, setActivities] = useState([]);
  const [schoolData, setSchoolData] = useState([]);

  useEffect(() => {
    axios.get(`${API_BASE}/districts`).then((r) => setDistricts(r.data)).catch(() => {});
    axios.get(`${API_BASE}/activities`).then((r) => setActivities(r.data)).catch(() => {});
  }, []);

  const fetchSchools = (district = selectedDistrict, activity = selectedActivity, fy = selectedFY) => {
    const fyStart = `${fy.split("-")[0]}-04-01`;
    const fyEnd = `${fy.split("-")[1]}-03-31`;

    axios
      .get(`${API_BASE}/custom-school-list`, {
        params: {
          district,
          activity,
          fy_start: fyStart,
          fy_end: fyEnd,
        },
      })
      .then((res) => setSchoolData(res.data))
      .catch((err) => {
        console.error(err);
        setSchoolData([]);
      });
  };

  useEffect(() => {
    fetchSchools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Frontend filter (server returns all schools with total_activities count)
  const filteredData =
    selectedStatus === "submitted"
      ? schoolData.filter((row) => Number(row.total_activities) > 0)
      : schoolData.filter((row) => Number(row.total_activities) === 0);

  const handleSearch = () => {
    localStorage.setItem(
      "viewSchoolsFilters",
      JSON.stringify({
        district: selectedDistrict,
        activity: selectedActivity,
        fy: selectedFY,
        status: selectedStatus,
      })
    );
    fetchSchools(selectedDistrict, selectedActivity, selectedFY);
  };

  const exportToExcel = () => {
    const sheetData = filteredData.map((row, index) => ({
      "Sl No": index + 1,
      "School Name": row.school_name,
      "Activities Submitted": Number(row.total_activities || 0),
      "Contact No": row.contact_no,
      "Contact Name": row.contact_name,
    }));
    const ws = XLSX.utils.json_to_sheet(sheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "SchoolList");
    XLSX.writeFile(wb, "Custom_Programme_Schools.xlsx");
  };

  const appliedDistrictName =
    selectedDistrict === "0" ? "All" : districts.find((d) => String(d.dist_id) === selectedDistrict)?.district || "All";
  const appliedActivityName =
    selectedActivity === "0" ? "All" : activities.find((a) => String(a.activities_id) === selectedActivity)?.activity || "All";

  return (
    <>
    <Helmet>
      <title>Custom Programme Status</title>
    </Helmet>
  
    <div style={{ padding: 30, background: "#f4f6f8", minHeight: "100vh" }}>
      <div style={{ background: "#fff", padding: 20, borderRadius: 12, marginBottom: 25, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <h2 style={{ margin: 0 }}>Custom Programme Submission Status</h2>

          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={() => navigate("/customProgramMonitor")}
              style={{ padding: "10px 18px", background: "#9e2c2c", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }}
            >
              Back
            </button>

            <button onClick={exportToExcel} style={{ padding: "10px 18px", background: "#2e7d32", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }}>
              Export to Excel
            </button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 20 }}>
          <label>
            District
            <select value={selectedDistrict} onChange={(e) => setSelectedDistrict(e.target.value)} style={{ display: "block", padding: 8, marginTop: 5 }}>
              <option value="0">All</option>
              {districts.map((d) => (
                <option key={d.dist_id} value={d.dist_id}>
                  {d.district}
                </option>
              ))}
            </select>
          </label>

          <label>
            Activity
            <select value={selectedActivity} onChange={(e) => setSelectedActivity(e.target.value)} style={{ display: "block", padding: 8, marginTop: 5 }}>
              <option value="0">All</option>
              {activities.map((a) => (
                <option key={a.activities_id} value={a.activities_id}>
                  {a.activity}
                </option>
              ))}
            </select>
          </label>

          <label>
            Financial Year
            <select value={selectedFY} onChange={(e) => setSelectedFY(e.target.value)} style={{ display: "block", padding: 8, marginTop: 5 }}>
              {[currentFY, "2023-2024", "2024-2025", "2025-2026"].map((fy) => (
                <option key={fy} value={fy}>
                  {fy}
                </option>
              ))}
            </select>
          </label>

          <label>
            Submission Status
            <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} style={{ display: "block", padding: 8, marginTop: 5 }}>
              <option value="submitted">Submitted</option>
              <option value="not_submitted">Not Submitted</option>
            </select>
          </label>

          <button onClick={handleSearch} style={{ padding: "10px 25px", background: "#1976d2", color: "#fff", borderRadius: 6, cursor: "pointer", marginTop: 25 }}>
            Search
          </button>
        </div>
      </div>

      <h3>
        Custom Programme Submission Status (
        District: <span style={{ color: "green" }}>{appliedDistrictName}</span> |
        Activity: <span style={{ color: "green" }}>{appliedActivityName}</span> |
        FY: <span style={{ color: "green" }}>{selectedFY}</span> |
        Status:{" "}
        <span style={{ color: selectedStatus === "submitted" ? "green" : "red", fontWeight: 700 }}>
          {selectedStatus === "submitted" ? "Submitted" : "Not Submitted"}
        </span>
        )
      </h3>

      <div style={{ background: "#fff", padding: 20, borderRadius: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.1)" }}>
        
      <style>{`
  .tblview th {
    text-align: left;
    padding: 12px;
    background: #283593;
    color: white;
  }
`}</style>
        <table className="tblview"  style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#283593", color: "white" }}>
              <th style={{ padding: 12 }}>Sl. No</th>
              <th style={{ padding: 12 }}>School Name</th>
              <th style={{ padding: 12 }}>Activities Submitted</th>
              <th style={{ padding: 12 }}>Contact No.</th>
              <th style={{ padding: 12 }}>Contact Name</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ textAlign: "center", padding: 20 }}>
                  No Records Found
                </td>
              </tr>
            ) : (
              filteredData.map((row, idx) => (
                <tr key={idx} style={{ borderBottom: "1px solid #ddd" }}>
                  <td style={{ padding: 12 }}>{idx + 1}</td>
                  <td style={{ padding: 12 }}>{row.school_name}</td>
                  <td style={{ padding: 12 }}>{Number(row.total_activities || 0)}</td>
                  <td style={{ padding: 12 }}>{row.contact_no}</td>
                  <td style={{ padding: 12 }}>{row.contact_name}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
    </>
  );
}
